import { Button, Tab, Tabs, TextField, Typography } from '@material-ui/core';
import { Save as SaveIcon, Close as CloseIcon } from '@material-ui/icons';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import * as Yup from 'yup';
import { ITask } from '../../../models/task.model';
import { AuthState } from '../../../state/auth';
import { AppState } from '../../../state/configureStore';
import { fetchRelatedSubtaskListByTaskId } from '../../../state/subtask';
import {
  clearTask,
  createTask,
  fetchTask,
  TaskState,
  updateTask,
} from '../../../state/task';
import { fetchTaskAttachmentListByTaskId } from '../../../state/taskAttachment';
import { fetchTaskItemListByTaskId } from '../../../state/taskItem';
import { Show } from '../../components';
import LogWorkDialog from './logWork/LogWorkDialog';
import { SubtaskAutoCreateDialog, SubtaskList } from './subtask';
import { TaskActivityComment, TaskActivityList } from './taskActivity';
import { TaskAttachment } from './taskAttachment';
import useStyles from './taskForm.style';
import { TaskItemList } from './taskItem';
import {
  Card as MUICard,
  Paper as MUIPaper,
  CardContent as MUICardContent,
  Stack as MUIStack,
  Box as MUIBox,
  Typography as MUITypography,
  Grid as MUIGrid,
  Paper,
} from '@mui/material';
import {
  AttachFileOutlined as AttachFileOutlinedIcon,
  ForumOutlined as ForumOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  NoteAltOutlined as NoteAltIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  FormatListBulletedOutlined as FormatListBulletedOutlinedIcon,
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  CalendarToday as CalendarTodayIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import CustomCaption from '../../components/CustomCaption';
import { TaskActivityState } from '../../../state/taskActivity/taskActivity.types';
import { fetchTaskActivityListByTaskId } from '../../../state/taskActivity';

const formPageUrl = `/tasks/form`;

const TaskSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required'),
  description: Yup.string().nullable(true),
  dueDate: Yup.date().required('Due Date is required').nullable(true),
});

interface TaskFormRouteParamsProps {
  id: string;
  subtaskId: string;
}

interface TaskFormProps extends RouteComponentProps<TaskFormRouteParamsProps> {}

interface TaskFormValues {
  id: ITask['id'];
  subject: string;
  description: string;
  dueDate: string | null;
  requester: ITask['requester'];
  requesterId: number;
}

const sectionCardSx = {
  borderRadius: 3,
  borderColor: 'rgba(124, 58, 237, 0.12)',
  backgroundColor: 'rgba(255,255,255,0.9)',
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
};

const fieldBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
};

const TaskForm: React.FC<TaskFormProps> = (props) => {
  const dispatch = useDispatch();
  const { match, history } = props;
  const {
    params: { id: paramsId },
  } = match;
  const isUpdateMode = paramsId !== undefined;
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(0);
  const [openLogWorkDialog, setOpenLogWorkDialog] = useState(false);
  const [openSubtaskAutoCreateDialog, setOpenSubtaskAutoCreateDialog] = useState(false);
  const { fullName, userId, user } = useSelector<AppState, AuthState>(
    (state) => state.auth,
  );
  const {
    item: {
      id,
      subject = '',
      taskNumber = '',
      description = '',
      dueDate = null,
      requester = user,
      requesterId = userId,
      createdAt = null,
    },
    totalSubtasks,
    totalTaskAttachments,
    totalTaskItems,
  } = useSelector<AppState, TaskState>((state) => state.task);
  const { totalItems: totalTaskActivities } = useSelector<AppState, TaskActivityState>(
    (state) => state.taskActivity,
  );

  const detailTabs = [
    {
      label: 'Subtasks',
      hint: 'Linked work items',
      count: totalSubtasks,
      icon: <AccountTreeOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Items',
      hint: 'Supporting entries',
      count: totalTaskItems,
      icon: <FormatListBulletedOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Activities',
      hint: 'Updates and comments',
      count: totalTaskActivities,
      icon: <ForumOutlinedIcon fontSize="small" />,
    },
    {
      label: 'Attachments',
      hint: 'Files and documents',
      count: totalTaskAttachments,
      icon: <AttachFileOutlinedIcon fontSize="small" />,
    },
  ];

  const initialValues: TaskFormValues = {
    id,
    subject,
    description,
    dueDate,
    requester,
    requesterId,
  };

  const redirectToTaskList = () => {
    if (history.length === 1) {
      history.replace('/tasks');
    } else {
      history.goBack();
    }
  };

  const afterCreateTaskCallback = () => {
    setOpenSubtaskAutoCreateDialog(true);
  };

  const afterCreateSubtaskAutoCallback = () => {
    history.push(`${formPageUrl}/${id}`);
  };

  const handleSubmit = (
    values: TaskFormValues,
    actions: FormikHelpers<TaskFormValues>,
  ) => {
    if (values.dueDate && !moment(values.dueDate, 'YYYY-MM-DD').isValid()) {
      actions.setFieldError('dueDate', 'Due date was not in a correct format');
    } else {
      if (isUpdateMode) {
        dispatch(updateTask({ values, callback: afterCreateTaskCallback }));
      } else {
        dispatch(createTask({ values, callback: afterCreateTaskCallback }));
      }
      actions.setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isUpdateMode) {
      dispatch(fetchTask(+paramsId));
    } else {
      dispatch(clearTask());
    }

    return function cleanUp() {
      dispatch(clearTask());
    };
  }, [paramsId, dispatch, isUpdateMode]);

  useEffect(() => {
    if (paramsId) {
      dispatch(fetchRelatedSubtaskListByTaskId(+paramsId));
      dispatch(fetchTaskItemListByTaskId(+paramsId));
      dispatch(fetchTaskAttachmentListByTaskId(+paramsId));
      dispatch(fetchTaskActivityListByTaskId(+paramsId));
    }
  }, [dispatch, paramsId]);

  return (
    <>
      <LogWorkDialog
        open={openLogWorkDialog}
        onClose={() => {
          setOpenLogWorkDialog(false);
        }}
        subtaskId={null}
        taskId={id}
        title="Log Duration Task"
      />
      <SubtaskAutoCreateDialog
        open={openSubtaskAutoCreateDialog}
        handleClose={() => {
          setOpenSubtaskAutoCreateDialog(false);
          afterCreateSubtaskAutoCallback();
        }}
      />
      {/* <Card className={classes.root}> */}
      <Formik<TaskFormValues>
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={TaskSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        isInitialValid={true}
      >
        {({ values, handleChange, errors, submitForm, setFieldValue }) => {
          return (
            <Form>
              <MUIPaper
                elevation={0}
                sx={{
                  p: { xs: 1.5, md: 2.5 },
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'rgba(124, 58, 237, 0.08)',
                  background:
                    'radial-gradient(circle at top, rgba(124,58,237,0.06), transparent 35%), linear-gradient(180deg, #fbfbff 0%, #f6f8ff 100%)',
                  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
                  marginTop: 4,
                }}
              >
                <MUIBox className={classes.formHeader}>
                  <Typography variant="h5" className={classes.title}>
                    {isUpdateMode ? `Update Task ${taskNumber}` : `New Task`}
                  </Typography>

                  <MUIBox className={classes.formActionShell}>
                    <Button
                      color="primary"
                      variant="outlined"
                      className={classes.headerSecondaryButton}
                      onClick={redirectToTaskList}
                      startIcon={<CloseIcon />}
                    >
                      Close
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      className={classes.headerPrimaryButton}
                      onClick={() => {
                        submitForm();
                      }}
                      startIcon={<SaveIcon />}
                    >
                      {!isUpdateMode ? `Create` : `Update`}
                    </Button>
                  </MUIBox>
                </MUIBox>

                {/* Task Information */}
                <MUICard variant="outlined" sx={{ ...sectionCardSx }}>
                  <MUICardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <MUIStack spacing={2.5}>
                      <MUIBox
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          pb: 1.5,
                          borderBottom: '1px solid rgba(124, 58, 237, 0.08)',
                        }}
                      >
                        <MUIBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2.5,
                            display: 'grid',
                            placeItems: 'center',
                            color: '#7c3aed',
                            background: 'rgba(124, 58, 237, 0.10)',
                          }}
                        >
                          <Inventory2OutlinedIcon fontSize="small" />
                        </MUIBox>
                        <MUIBox>
                          <MUITypography
                            variant="h6"
                            sx={{ fontWeight: 700, color: '#4338ca' }}
                          >
                            Task Information
                          </MUITypography>
                          <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
                            Manage your task details, add description and set due date.
                          </MUITypography>
                        </MUIBox>
                      </MUIBox>

                      {/* Task Details */}
                      <MUIGrid container spacing={1}>
                        <MUIGrid item xs={12} md={6} sx={fieldBlockSx}>
                          {/* Subject */}
                          <CustomCaption
                            caption={'Subject'}
                            icon={
                              <NoteAltIcon
                                fontSize="small"
                                style={{ color: '#2563eb' }}
                              />
                            }
                          />
                          <TextField
                            name="subject"
                            type="input"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={values.subject}
                            onChange={handleChange}
                            error={!!errors.subject}
                            helperText={errors.subject}
                          />
                        </MUIGrid>
                        <MUIGrid item xs={12} sm={12} md={2} sx={fieldBlockSx}>
                          {/* Requester */}
                          <CustomCaption
                            caption={'Requester'}
                            icon={
                              <PersonOutlineIcon
                                fontSize="small"
                                style={{ color: '#f59e0b' }}
                              />
                            }
                          />
                          <TextField
                            name="Requester"
                            type="input"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={requester ? `${requester.fullName}` : `${fullName}`}
                            disabled
                          />
                        </MUIGrid>
                        <MUIGrid item xs={12} sm={6} md={2} sx={fieldBlockSx}>
                          {/* Created Date */}
                          <CustomCaption
                            caption={'Created Date'}
                            icon={
                              <CalendarTodayIcon
                                fontSize="small"
                                style={{ color: '#10b981' }}
                              />
                            }
                          />
                          <Show show={createdAt !== null}>
                            <KeyboardDatePicker
                              fullWidth={true}
                              inputVariant="outlined"
                              margin="dense"
                              format="yyyy-MM-dd"
                              value={createdAt}
                              onChange={() => {}}
                              disabled
                            />
                          </Show>
                        </MUIGrid>
                        <MUIGrid item xs={12} sm={6} md={2} sx={fieldBlockSx}>
                          {/* Due Date */}
                          <CustomCaption
                            caption={'Due Date'}
                            icon={
                              <EventNoteIcon
                                fontSize="small"
                                style={{ color: '#ef4444' }}
                              />
                            }
                          />
                          <KeyboardDatePicker
                            fullWidth={true}
                            inputVariant="outlined"
                            margin="dense"
                            format="yyyy-MM-dd"
                            value={values.dueDate}
                            onChange={(selectedDate, selectedStringDate) => {
                              setFieldValue('dueDate', selectedStringDate);
                            }}
                            error={!!errors.dueDate}
                            helperText={errors.dueDate}
                          />
                        </MUIGrid>
                        <MUIGrid item xs={12} sx={fieldBlockSx}>
                          {/* Description */}
                          <CustomCaption
                            caption={'Description'}
                            icon={
                              <DescriptionOutlinedIcon
                                fontSize="small"
                                style={{ color: '#06b6d4' }}
                              />
                            }
                          />
                          <TextField
                            name="description"
                            type="input"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={values.description}
                            onChange={handleChange}
                            error={!!errors.description}
                            helperText={errors.description}
                            multiline
                            rows="5"
                          />
                        </MUIGrid>
                      </MUIGrid>
                    </MUIStack>
                  </MUICardContent>
                </MUICard>

                {/* Task Detail Information */}
                <MUICard variant="outlined" sx={{ ...sectionCardSx, mt: 3 }}>
                  <MUICardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                    <MUIStack spacing={2.5}>
                      <MUIBox
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          pb: 1.5,
                          borderBottom: '1px solid rgba(124, 58, 237, 0.08)',
                        }}
                      >
                        <MUIBox
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2.5,
                            display: 'grid',
                            placeItems: 'center',
                            color: '#2563eb',
                            background: 'rgba(37, 99, 235, 0.10)',
                          }}
                        >
                          <Inventory2OutlinedIcon fontSize="small" />
                        </MUIBox>
                        <MUIBox>
                          <MUITypography
                            variant="h6"
                            sx={{ fontWeight: 700, color: '#2563eb' }}
                          >
                            Task Detail Information
                          </MUITypography>
                          <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
                            Additional details about your task.
                          </MUITypography>
                        </MUIBox>
                      </MUIBox>

                      {/* Tab Details */}
                      <MUIGrid container spacing={1}>
                        {id !== undefined && (
                          <MUIGrid item xs={12}>
                            <MUIBox className={classes.tabSection}>
                              <Tabs
                                value={tabIndex}
                                indicatorColor="primary"
                                textColor="primary"
                                onChange={(event, newIndex) => setTabIndex(newIndex)}
                                variant="scrollable"
                                scrollButtons="auto"
                                className={classes.tab}
                              >
                                {detailTabs.map((tab) => (
                                  <Tab
                                    key={tab.label}
                                    label={
                                      <span className={classes.tabLabel}>
                                        <span className={classes.tabIcon}>
                                          {tab.icon}
                                        </span>
                                        <span className={classes.tabTextGroup}>
                                          <span className={classes.tabText}>
                                            {tab.label}
                                          </span>
                                          <span className={classes.tabHint}>
                                            {tab.hint}
                                          </span>
                                        </span>
                                        {typeof tab.count === 'number' && (
                                          <span className={classes.tabCount}>
                                            {tab.count}
                                          </span>
                                        )}
                                      </span>
                                    }
                                  />
                                ))}
                                {/* <Tab label="Log Works" /> */}
                              </Tabs>
                            </MUIBox>
                          </MUIGrid>
                        )}
                        {isUpdateMode && tabIndex === 0 && (
                          <MUIGrid item xs={12} sx={{ width: '100%' }}>
                            <SubtaskList smTaskId={+paramsId} />
                          </MUIGrid>
                        )}
                        {isUpdateMode && tabIndex === 1 && (
                          <MUIGrid item xs={12} sx={{ width: '100%' }}>
                            <TaskItemList smTaskId={id} />
                          </MUIGrid>
                        )}
                        {isUpdateMode && tabIndex === 2 && (
                          <MUIGrid item xs={12} sx={{ width: '100%' }}>
                            <Paper
                              elevation={2}
                              style={{ padding: 12, borderRadius: 16 }}
                            >
                              <TaskActivityComment smTaskId={+paramsId} />
                              <TaskActivityList smTaskId={+paramsId} />
                            </Paper>
                          </MUIGrid>
                        )}
                        {isUpdateMode && tabIndex === 3 && (
                          <MUIGrid item xs={12} sx={{ width: '100%' }}>
                            <TaskAttachment smTaskId={+paramsId} />
                          </MUIGrid>
                        )}
                      </MUIGrid>
                    </MUIStack>
                  </MUICardContent>
                </MUICard>
              </MUIPaper>

              {/* <Typography variant="h5" className={classes.title}>
                  {isUpdateMode ? `Update Task ${taskNumber}` : `New Task`}
                </Typography>
                <Grid item xs={12}>
                  <div className={classes.buttonGroup}>
                    <Button
                      color="primary"
                      variant="outlined"
                      className={classes.button}
                      onClick={redirectToTaskList}
                      startIcon={<CloseIcon />}
                    >
                      Close
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      className={classes.button}
                      onClick={() => {
                        submitForm();
                      }}
                      startIcon={<SaveIcon />}
                    >
                      {!isUpdateMode ? `Create` : `Update`}
                    </Button>
                  </div>
                </Grid>
                <Loading isLoading={loading}>
                  <CardContent>
                    <TextField
                      name="subject"
                      type="input"
                      variant="outlined"
                      label="Subject"
                      margin="dense"
                      fullWidth
                      value={values.subject}
                      onChange={handleChange}
                      error={!!errors.subject}
                      helperText={errors.subject}
                    />
                    <TextField
                      name="description"
                      type="input"
                      // label="Description"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={values.description}
                      onChange={handleChange}
                      error={!!errors.description}
                      helperText={errors.description}
                      multiline
                      rows="5"
                    />
                    <TextField
                      name="Requester"
                      type="input"
                      label="Requester"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={requester ? `${requester.fullName}` : `${fullName}`}
                      disabled
                    />
                    <Grid item xs={12}>
                      <Show show={createdAt !== null}>
                        <KeyboardDatePicker
                          fullWidth={true}
                          margin="normal"
                          label="Created Date"
                          format="yyyy-MM-dd"
                          value={createdAt}
                          onChange={() => {}}
                          disabled
                        />
                      </Show>
                    </Grid>
                    <Grid item xs={12}>
                      <KeyboardDatePicker
                        fullWidth={true}
                        margin="normal"
                        label="Due Date"
                        format="yyyy-MM-dd"
                        value={values.dueDate}
                        onChange={(selectedDate, selectedStringDate) => {
                          setFieldValue('dueDate', selectedStringDate);
                        }}
                        error={!!errors.dueDate}
                        helperText={errors.dueDate}
                      />
                    </Grid>
                  </CardContent>
                </Loading> */}
            </Form>
          );
        }}
      </Formik>
      {/* </Card> */}
      {/* {id !== undefined && (
        <Card className={classes.root}>
          <Tabs
            value={tabIndex}
            indicatorColor="primary"
            textColor="primary"
            onChange={(event, newIndex) => setTabIndex(newIndex)}
            variant="scrollable"
            className={classes.tab}
          >
            <Tab
              label={
                <Badge
                  className={classes.badge}
                  color="primary"
                  badgeContent={totalSubtasks}
                >
                  Subtasks
                </Badge>
              }
            />
            <Tab
              label={
                <Badge
                  className={classes.badge}
                  color="primary"
                  badgeContent={totalTaskItems}
                >
                  Items
                </Badge>
              }
            />
            <Tab label="Activities" />
            <Tab
              label={
                <Badge
                  className={classes.badge}
                  color="primary"
                  badgeContent={totalTaskAttachments}
                >
                  Attachments
                </Badge>
              }
            />
          </Tabs>
        </Card>
      )}
      {isUpdateMode && tabIndex === 0 && <SubtaskList smTaskId={+paramsId} />}
      {isUpdateMode && tabIndex === 1 && <TaskItemList smTaskId={id} />}
      {isUpdateMode && tabIndex === 2 && (
        <Card className={classes.activity}>
          <TaskActivityComment smTaskId={+paramsId} />
          <TaskActivityList smTaskId={+paramsId} />
        </Card>
      )}
      {isUpdateMode && tabIndex === 3 && <TaskAttachment smTaskId={+paramsId} />} */}

      {/* {isUpdateMode && tabIndex === 4 && <LogWorkList smTaskId={+paramsId} />} */}
    </>
  );
};

export default TaskForm;
